/**
 * Aerodrome · visual docking.
 *
 * Every stand on every apron with the docking unit at its head: what the
 * unit is, whether it is guiding, what its display shows now, who is on the
 * stand and who comes next — and whether the next arrival will need a
 * marshaller, because the unit is down, because there is none, or because
 * it was never taught that aircraft type.
 *
 * Picking a stand opens its unit: calibration and emergency-stop test, the
 * types in its database, the last day's dockings with the stop error of
 * each, and the actions a supervisor takes — record a calibration, change
 * the status with a reason, teach a type, raise a work order against it.
 *
 * The unit register is kept on the platform one unit to a row (build 5), so
 * two supervisors changing two units never overwrite each other, and two
 * changing the same unit are told. The stands and their aprons come from the
 * allocation's stand register, and the traffic from the flight record, so a
 * stand added there appears here with "no unit recorded".
 *
 * Dockings are what a unit logs, not what anybody enters: they are not kept
 * in the row (keeping the sample's there froze them on the day of the first
 * save). The demo shows the sample's log; the platform has no unit feed yet.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { useCreateWorkOrder, useWorkOrders } from "../../api/queries";
import { Button, Card, EmptyState, Field, Input, Segmented, Select } from "../../design/components";
import { flightName, type Flight } from "../../domain/flights";
import type { Stand, StandRegister } from "../../domain/stands";
import {
  APRON_LABEL,
  attention,
  byApron,
  calibration,
  COVER_HOURS,
  COVER_LABEL,
  displayText,
  guides,
  KIND_LABEL,
  marshallerCover,
  RESULT_LABEL,
  standTraffic,
  STATUS_LABEL,
  STOP_TOLERANCE_CM,
  unitFor,
  unitStats,
  vdgsSummary,
  dockingPerformance,
  dockingStands,
  type VdgsStatus,
  type VdgsUnit,
} from "../../domain/vdgs";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { hhmm } from "../../lib/format";
import { mockVdgs } from "../../mock/vdgs";
import { Tiles } from "../executive/shared";
import { VDGS_SPEC } from "../../domain/registerSpecs";
import { RegisterNotices } from "../shared/RegisterNotices";
import { useRegisterDoc } from "../shared/useRegisterRows";
import { DayState } from "../flights/FlightTheme";
import { useFlightWorld } from "../flights/useFlightWorld";
import s from "./Vdgs.module.css";

const day = (iso: string | undefined) => (iso ? iso.slice(0, 10) : "—");
type Show = "all" | "attention" | "down";

export function VdgsPage() {
  const session = useSession();
  const world = useFlightWorld();
  const vdgs = useRegisterDoc(VDGS_SPEC);
  const flights = world.flights;
  const register = world.register;
  /* the docking log: the sample's on the demo, read fresh against the clock; none kept on the platform yet */
  const log = useMemo(
    () => new Map(session.source.kind === "mock" ? mockVdgs().units.map((u) => [u.stand, u.dockings]) : []),
    [session.source.kind],
  );
  const units = useMemo(
    () => vdgs.value.units.map((u) => ({ ...u, dockings: log.get(u.stand) ?? [] })),
    [vdgs.value, log],
  );
  const now = new Date();

  const [apron, setApron] = useState<string>("all");
  const [show, setShow] = useState<Show>("all");
  const [picked, setPicked] = useState<string | null>(null);

  const stands = useMemo(() => dockingStands(register.stands, units), [register.stands, units]);
  const groups = useMemo(() => byApron(stands), [stands]);
  const summary = vdgsSummary(stands, units, now);
  const performance = dockingPerformance(units, now, stands);
  const cover = marshallerCover(register, flights, units, now);

  /* one unit changed: only its row is sent, from the version on screen */
  const save = (next: VdgsUnit) => {
    const base = vdgs.value;
    void vdgs.save({ units: [...base.units.filter((u) => u.stand !== next.stand), { ...next, dockings: [] }] }, base);
  };

  const visible = (st: Stand) => {
    const u = unitFor(units, st.code);
    if (show === "down") return !!u && (u.status === "fault" || u.status === "maintenance");
    if (show === "attention") return attention(u, now).level !== "good";
    return true;
  };

  useScreenContext({
    route: "/vdgs",
    title: "Visual docking",
    summary: [
      t("{g} of {f} docking units guiding across {s} stands.", {
        g: summary.guiding,
        f: summary.fitted,
        s: summary.stands,
      }),
      t("{n} arrivals in the next {h} hours need a marshaller.", { n: cover.length, h: COVER_HOURS }),
      t("{n} units due or overdue for calibration.", { n: summary.calibrationDue }),
    ],
    offers: [
      { label: t("Which stands need a marshaller?"), ask: "which arrivals need a marshaller because of docking?" },
    ],
    worksInFull: true,
  });

  const src = [{ kind: "entered" as const, ref: "vdgs", label: t("the docking unit register") }];
  const tiles = [
    {
      key: "guiding",
      label: t("units guiding"),
      value: summary.guiding,
      unit: "",
      text: t("of {n} fitted, on {s} stands", { n: summary.fitted, s: summary.stands }),
      tone: summary.guiding < summary.fitted ? ("warn" as const) : ("good" as const),
    },
    {
      key: "performance",
      label: t("docking performance"),
      value: performance.overall == null ? null : Math.round(performance.overall * 100),
      unit: "%",
      text: t("across {n} units, all aprons (gauge on Instruments)", { n: performance.units }),
      tone:
        performance.overall == null
          ? undefined
          : performance.overall < 0.4
            ? ("bad" as const)
            : performance.overall < 0.7
              ? ("warn" as const)
              : ("good" as const),
    },
    {
      key: "down",
      label: t("fault or maintenance"),
      value: summary.down,
      unit: "",
      text: t("not bringing aircraft in"),
      tone: summary.down ? ("bad" as const) : ("good" as const),
    },
    {
      key: "cover",
      label: t("marshaller needed"),
      value: cover.length,
      unit: "",
      text: t("arrivals in the next {h} hours", { h: COVER_HOURS }),
      tone: cover.length ? ("warn" as const) : ("good" as const),
    },
    {
      key: "cal",
      label: t("calibration due"),
      value: summary.calibrationDue,
      unit: "",
      text: t("overdue, or within 14 days"),
      tone: summary.calibrationDue ? ("warn" as const) : ("good" as const),
    },
    {
      key: "accuracy",
      label: t("on the mark"),
      value: summary.accuracy,
      unit: "%",
      text: t("of {n} dockings in 24 h, within {c} cm", { n: summary.dockings, c: STOP_TOLERANCE_CM }),
      tone: summary.accuracy != null && summary.accuracy < 90 ? ("warn" as const) : ("good" as const),
    },
    {
      key: "aborts",
      label: t("aborted dockings"),
      value: summary.aborts,
      unit: "",
      text: t("in the last 24 hours"),
      tone: summary.aborts ? ("bad" as const) : ("good" as const),
    },
  ];

  const pickedStand = picked ? stands.find((x) => x.code === picked) : undefined;

  const lede = (
    <p className={s.lede}>
      {t(
        "Docking guidance on every stand: its status, display, and traffic, and which arrivals need a marshaller.",
      )}
    </p>
  );
  /* the stands come with the plan: until it is read, the register would be the units alone */
  if (world.loading || world.error)
    return (
      <Page eyebrow={t("{site} · Aerodrome", { site: session.site.code })} title="Visual docking">
        {lede}
        <DayState world={world} />
      </Page>
    );

  return (
    <Page eyebrow={t("{site} · Aerodrome", { site: session.site.code })} title="Visual docking">
      {lede}
      <RegisterNotices doc={vdgs} what={t("docking unit register")} />
      {/* no flights on the record: the units and their stands are still worth seeing */}
      <DayState world={world} />
      <Tiles items={tiles.map((x) => ({ ...x, assumed: true, sources: src }))} currency="SAR" />

      <Card
        title="Marshaller needed"
        sub={t("Arrivals in the next {h} hours with no guidance", { h: COVER_HOURS })}
        testId="vdgs-cover"
      >
        {cover.length === 0 ? (
          <EmptyState>{t("Every upcoming arrival has working guidance.")}</EmptyState>
        ) : (
          <ul className={s.cover}>
            {cover.map((c) => (
              <li key={`${c.stand}-${c.flight.id}`} data-testid={`cover-${c.stand}`} data-why={c.why}>
                <span className={s.coverTime}>{hhmm(c.at)}</span>
                <b>{flightName(c.flight)}</b>
                <span className={s.dim}>{c.flight.aircraftIcao ?? ""}</span>
                <button type="button" className={s.linkBtn} onClick={() => setPicked(c.stand)}>
                  {t("stand {s}", { s: c.stand })}
                </button>
                <span className={s.coverWhy} data-why={c.why}>
                  {t(COVER_LABEL[c.why])}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className={s.bar}>
        <Segmented
          ariaLabel="Apron"
          value={apron}
          onChange={setApron}
          options={[{ value: "all", label: "All aprons" }, ...groups.map((g) => ({ value: g.apron, label: g.label }))]}
        />
        <Segmented
          ariaLabel="Show"
          value={show}
          onChange={setShow}
          options={[
            { value: "all", label: "Every stand" },
            { value: "attention", label: "Needs attention" },
            { value: "down", label: "Not guiding" },
          ]}
        />
        <Link to="/stands" className={s.dim}>
          {t("Stand plan →")}
        </Link>
        <Link to="/instruments" className={s.dim}>
          {t("Performance gauge on Instruments →")}
        </Link>
      </div>

      {pickedStand && (
        <UnitPanel
          stand={pickedStand}
          unit={unitFor(units, pickedStand.code)}
          register={register}
          flights={flights}
          now={now}
          onSave={save}
          onClose={() => setPicked(null)}
        />
      )}

      {groups
        .filter((g) => apron === "all" || g.apron === apron)
        .map((g) => {
          const shown = g.stands.filter(visible);
          const guiding = g.stands.filter((st) => guides(unitFor(units, st.code))).length;
          const perf = performance.aprons.find((a) => a.apron === g.apron);
          return (
            <section key={g.apron} className={s.apron} data-testid={`apron-${g.apron}`}>
              <header className={s.apronHead}>
                <h2>{t(g.label)}</h2>
                <span className={s.dim}>
                  {t("{n} stands · {g} guided", { n: g.stands.length, g: guiding })}
                  {perf?.score != null && ` · ${t("performance {p}%", { p: Math.round(perf.score * 100) })}`}
                </span>
              </header>
              {shown.length === 0 ? (
                <p className={s.dim}>{t("Nothing to show on this apron.")}</p>
              ) : (
                <div className={s.grid}>
                  {shown.map((st) => (
                    <StandCard
                      key={st.code}
                      stand={st}
                      unit={unitFor(units, st.code)}
                      register={register}
                      flights={flights}
                      now={now}
                      picked={picked === st.code}
                      onPick={() => setPicked(picked === st.code ? null : st.code)}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}

      <p className={s.note}>
        {t(
          "Demo data. The {d}-day calibration interval and {c} cm stop tolerance are proposed. The unit's manual governs.",
          { d: 180, c: STOP_TOLERANCE_CM },
        )}
      </p>
    </Page>
  );
}

function tone(u: VdgsUnit | undefined, now: Date): string {
  if (!u || u.kind === "marshaller" || u.kind === "none") return "none";
  if (u.status === "fault") return "fault";
  if (u.status === "maintenance") return "maint";
  return attention(u, now).level;
}

function StandCard({
  stand,
  unit,
  register,
  flights,
  now,
  picked,
  onPick,
}: {
  stand: Stand;
  unit: VdgsUnit | undefined;
  register: StandRegister;
  flights: readonly Flight[];
  now: Date;
  picked: boolean;
  onPick: () => void;
}) {
  const traffic = standTraffic(stand.code, register, flights, now);
  const shown = displayText(unit, traffic, now);
  const cal = unit ? calibration(unit, now) : null;
  const stats = unit ? unitStats(unit, now) : null;
  const fitted = !!unit && (unit.kind === "a_vdgs" || unit.kind === "vdgs");
  const unknownType =
    fitted && traffic.next?.flight.aircraftIcao && !unit.aircraftTypes.includes(traffic.next.flight.aircraftIcao);

  return (
    <button
      type="button"
      className={s.stand}
      data-tone={tone(unit, now)}
      aria-pressed={picked}
      onClick={onPick}
      data-testid={`vdgs-${stand.code}`}
    >
      <div className={s.standTop}>
        <span className={s.code}>{stand.code}</span>
        <span className={s.gate}>
          {stand.gate ? t("gate {g}", { g: stand.gate }) : stand.kind === "remote" ? t("remote") : t("no gate")}
        </span>
        {fitted ? (
          <span className={s.display} data-show={shown} aria-label={t("What the display shows")}>
            {shown}
          </span>
        ) : (
          <span className={s.marshal} title={t("Marshalled by hand")}>
            ✋
          </span>
        )}
      </div>
      <div className={s.status} data-status={unit?.status ?? "none"}>
        {unit ? t(fitted ? STATUS_LABEL[unit.status] : KIND_LABEL[unit.kind]) : t("No unit recorded")}
      </div>
      {fitted && (
        <div className={s.model}>
          {[unit.make, unit.model].filter(Boolean).join(" ")}
          {unit.fault && <div className={s.faultLine}>{unit.fault.what}</div>}
        </div>
      )}
      <dl className={s.facts}>
        <dt>{t("On stand")}</dt>
        <dd>
          {traffic.now
            ? `${flightName(traffic.now.flight)} · ${traffic.now.flight.aircraftIcao ?? ""} · ${t("until {t}", { t: hhmm(traffic.now.to) })}`
            : t("empty")}
        </dd>
        <dt>{t("Next")}</dt>
        <dd className={unknownType ? s.warnText : undefined}>
          {traffic.next
            ? `${hhmm(traffic.next.from)} · ${flightName(traffic.next.flight)} · ${traffic.next.flight.aircraftIcao ?? ""}`
            : t("nothing planned")}
          {unknownType && ` — ${t("type not taught")}`}
        </dd>
        {cal && cal.state !== "n/a" && (
          <>
            <dt>{t("Calibration")}</dt>
            <dd className={cal.state === "overdue" ? s.badText : cal.state === "soon" ? s.warnText : undefined}>
              {cal.state === "never"
                ? t("none on record")
                : cal.state === "overdue"
                  ? t("overdue {n} days", { n: -(cal.daysLeft ?? 0) })
                  : t("due {d}", { d: day(cal.due) })}
            </dd>
          </>
        )}
        {stats && fitted && (
          <>
            <dt>{t("Last 24 h")}</dt>
            <dd>
              {t("{n} dockings", { n: stats.dockings })}
              {stats.aborts ? ` · ${t("{n} aborted", { n: stats.aborts })}` : ""}
              {stats.averageErrorCm != null ? ` · ±${stats.averageErrorCm} cm` : ""}
            </dd>
          </>
        )}
      </dl>
    </button>
  );
}

function UnitPanel({
  stand,
  unit,
  register,
  flights,
  now,
  onSave,
  onClose,
}: {
  stand: Stand;
  unit: VdgsUnit | undefined;
  register: StandRegister;
  flights: readonly Flight[];
  now: Date;
  onSave: (u: VdgsUnit) => void;
  onClose: () => void;
}) {
  const create = useCreateWorkOrder();
  const orders = useWorkOrders();
  const [status, setStatus] = useState<VdgsStatus>(unit?.status ?? "in_service");
  const [reason, setReason] = useState("");
  const [type, setType] = useState("");
  const [raised, setRaised] = useState<string | null>(null);
  const traffic = standTraffic(stand.code, register, flights, now);
  const att = attention(unit, now);
  const cal = unit ? calibration(unit, now) : null;
  const stats = unit ? unitStats(unit, now) : null;
  const fitted = !!unit && (unit.kind === "a_vdgs" || unit.kind === "vdgs");
  const tag = unit?.tag ?? `VDGS-${stand.code}`;
  const open = (orders.data?.items ?? []).filter(
    (w) => w.assetTag === tag && !["completed", "verified", "cancelled"].includes(w.status),
  );

  const base: VdgsUnit = unit ?? {
    stand: stand.code,
    apron: stand.zoneCode,
    tag,
    kind: "a_vdgs",
    status: "in_service",
    aircraftTypes: [],
    dockings: [],
  };
  const today = new Date().toISOString();

  const raise = () => {
    create.mutate(
      {
        title: t("Docking guidance, stand {s}: {what}", {
          s: stand.code,
          what: unit?.fault?.what ?? att.reasons[0] ?? t("inspect and calibrate"),
        }),
        kind: unit?.status === "fault" ? "corrective" : "preventive",
        priority: unit?.status === "fault" ? 2 : 3,
        assetTag: tag,
        zoneCode: stand.zoneCode,
        description: [
          t("Stand {s} ({apron})", { s: stand.code, apron: stand.zoneCode ?? "" }),
          [base.make, base.model].filter(Boolean).join(" "),
          ...att.reasons,
          traffic.next
            ? t("Next arrival: {f} at {t}", { f: flightName(traffic.next.flight), t: hhmm(traffic.next.from) })
            : "",
        ]
          .filter(Boolean)
          .join("\n"),
      },
      {
        onSuccess: (wo) => {
          setRaised(wo.number);
          if (unit?.fault) onSave({ ...unit, fault: { ...unit.fault, workOrder: wo.number } });
        },
      },
    );
  };

  return (
    <Card
      title={t("Stand {s} — docking unit", { s: stand.code })}
      sub={`${t(APRON_LABEL[stand.zoneCode ?? ""] ?? stand.zoneCode ?? "")} · ${unit ? t(KIND_LABEL[unit.kind]) : t("No unit recorded")}`}
      actions={
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t("Close")}
        </Button>
      }
      testId="vdgs-panel"
    >
      <div className={s.panel}>
        <div>
          {att.reasons.length > 0 && (
            <ul className={s.reasons} data-level={att.level}>
              {att.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
          <dl className={s.facts}>
            <dt>{t("Unit")}</dt>
            <dd>{[base.make, base.model].filter(Boolean).join(" ") || "—"}</dd>
            <dt>{t("Asset tag")}</dt>
            <dd className={s.mono}>{tag}</dd>
            <dt>{t("Installed")}</dt>
            <dd>{day(unit?.installed)}</dd>
            <dt>{t("Last calibrated")}</dt>
            <dd>
              {day(unit?.lastCalibration)}
              {cal?.due ? ` · ${t("due {d}", { d: day(cal.due) })}` : ""}
            </dd>
            <dt>{t("Emergency stop tested")}</dt>
            <dd>{day(unit?.lastEStopTest)}</dd>
            <dt>{t("Bridge interlock")}</dt>
            <dd>{unit?.bridgeInterlock ? t("yes — the bridge waits for STOP") : t("no")}</dd>
            <dt>{t("Aircraft types taught")}</dt>
            <dd>{unit?.aircraftTypes.length ? unit.aircraftTypes.join(", ") : "—"}</dd>
            {unit?.fault?.workOrder && (
              <>
                <dt>{t("Work order")}</dt>
                <dd>
                  <Link to="/work-orders">{unit.fault.workOrder}</Link>
                </dd>
              </>
            )}
            {open.length > 0 && (
              <>
                <dt>{t("Open work")}</dt>
                <dd>
                  {open.map((w) => (
                    <Link key={w.id} to={`/work-orders/${w.id}`} className={s.woLink}>
                      {w.number}
                    </Link>
                  ))}
                </dd>
              </>
            )}
          </dl>
          {unit?.notes && <p className={s.dim}>{unit.notes}</p>}
        </div>

        <div className={s.actions}>
          <Field label="Status">
            <Select value={status} onChange={(e) => setStatus(e.target.value as VdgsStatus)} data-testid="vdgs-status">
              {(Object.keys(STATUS_LABEL) as VdgsStatus[]).map((k) => (
                <option key={k} value={k}>
                  {t(STATUS_LABEL[k])}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Why">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("what is wrong, or what was done")}
              data-testid="vdgs-reason"
            />
          </Field>
          <Button
            size="sm"
            variant="primary"
            data-testid="vdgs-status-save"
            disabled={status !== "in_service" && status !== "not_fitted" && !reason.trim()}
            onClick={() => {
              onSave({
                ...base,
                status,
                /* a unit put into service where there was none is a fitted unit from now on */
                kind:
                  status !== "not_fitted" && (base.kind === "marshaller" || base.kind === "none")
                    ? "a_vdgs"
                    : base.kind,
                fault:
                  status === "in_service" || status === "not_fitted"
                    ? undefined
                    : { since: today, what: reason.trim(), workOrder: base.fault?.workOrder },
                notes: reason.trim() && status === "in_service" ? reason.trim() : base.notes,
              });
              setReason("");
            }}
          >
            {t("Save status")}
          </Button>
          {fitted && (
            <>
              <Button
                size="sm"
                data-testid="vdgs-calibrated"
                onClick={() =>
                  onSave({
                    ...base,
                    lastCalibration: today,
                    status: base.status === "maintenance" ? "in_service" : base.status,
                    fault: base.status === "maintenance" ? undefined : base.fault,
                  })
                }
              >
                {t("Calibrated today")}
              </Button>
              <Button size="sm" onClick={() => onSave({ ...base, lastEStopTest: today })}>
                {t("Emergency stop tested today")}
              </Button>
              <div className={s.teach}>
                <Input
                  value={type}
                  onChange={(e) => setType(e.target.value.toUpperCase().slice(0, 4))}
                  placeholder={t("ICAO type, e.g. A332")}
                  data-testid="vdgs-type"
                />
                <Button
                  size="sm"
                  disabled={type.length < 3 || base.aircraftTypes.includes(type)}
                  data-testid="vdgs-type-add"
                  onClick={() => {
                    onSave({ ...base, aircraftTypes: [...base.aircraftTypes, type] });
                    setType("");
                  }}
                >
                  {t("Add type")}
                </Button>
              </div>
            </>
          )}
          <Button size="sm" onClick={raise} disabled={create.isPending} data-testid="vdgs-raise">
            {t("New Work Order")}
          </Button>
          {raised && (
            <p className={s.ok} data-testid="vdgs-raised">
              {t("Raised {n}", { n: raised })} — <Link to="/work-orders">{t("open work orders")}</Link>
            </p>
          )}
          {create.isError && <p className={s.badText}>{String(create.error)}</p>}
        </div>
      </div>

      {stats && fitted && (
        <>
          <h3 className={s.h3}>{t("The last day's dockings")}</h3>
          {unit.dockings.length === 0 ? (
            <EmptyState>{t("No dockings logged.")}</EmptyState>
          ) : (
            <table className={s.table} data-testid="vdgs-dockings">
              <thead>
                <tr>
                  <th>{t("Time")}</th>
                  <th>{t("Aircraft")}</th>
                  <th>{t("Result")}</th>
                  <th className={s.num}>{t("Stop error")}</th>
                  <th className={s.num}>{t("Seconds")}</th>
                  <th>{t("Note")}</th>
                </tr>
              </thead>
              <tbody>
                {[...unit.dockings]
                  .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
                  .map((d) => (
                    <tr key={d.at} data-result={d.result}>
                      <td className={s.mono}>{hhmm(d.at)}</td>
                      <td>{d.flight ? `${d.flight} · ${d.aircraft}` : d.aircraft}</td>
                      <td>
                        <span className={s.result} data-result={d.result}>
                          {t(RESULT_LABEL[d.result])}
                        </span>
                      </td>
                      <td className={s.num}>
                        {d.result === "abort" || d.result === "marshalled"
                          ? "—"
                          : `${d.stopErrorCm > 0 ? "+" : ""}${d.stopErrorCm} cm`}
                      </td>
                      <td className={s.num}>{d.seconds || "—"}</td>
                      <td className={s.dim}>{d.note ?? ""}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </Card>
  );
}
